import { useCallback } from 'react';

import { Divider, Icon, Tooltip } from '@treezor/design-system';
import { useTranslation } from 'react-i18next';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { useAccesControl } from '@/hooks/useAccessControl';
import { useAppDispatch } from '@/hooks/redux-helpers';
import { ITransactionByDigitizedCard } from '@/features/cards/store/models/card.model';
import { openModal } from '@/store/actions/core.actions';
import { featuresSlugs } from '@/features/roles/store/models/features.model';
import { IPaymentCardSettingsProps } from '@/features/cards/components/CardSettings/PaymentCardSettingsModal';
import { useProfileContext } from '@/features/context/ProfileContext';

export function DigitizedCard({ transaction }: { transaction: ITransactionByDigitizedCard }) {
  const { t } = useTranslation();
  const dashboardConfig = useDashboardConfig();
  const { hasAccessToFeature } = useAccesControl();
  const dispatch = useAppDispatch();
  const { user } = useProfileContext();

  const hasDigitizedCardConfig = dashboardConfig?.settings.cards.digitizedCards;

  const togglePaymentCardSettingsDialog = useCallback(
    () =>
      !!user &&
      dispatch(
        openModal<IPaymentCardSettingsProps>(
          'paymentCardSettingsModal',
          {
            activeMenu: 'digitized_cards',
            user,
            cardDigitalizationExternalId: transaction.cardDigitalizationExternalId,
          },
          { fullWidth: true, maxWidth: 'lg' },
        ),
      ),
    [user, dispatch, transaction.cardDigitalizationExternalId],
  );

  return (
    <>
      {hasDigitizedCardConfig && transaction?.isLinkedToDizitedcard && hasAccessToFeature(featuresSlugs.EDIT_CARDS) ? (
        <Tooltip
          messageContent={
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="flex align-baseline items-center justify-between w-full gap-3">
                  <span className="typo-body-regular-small min-w-[5rem]">
                    {t('pages.currentProfiles.cards.settings.digitizedCards.headers.walletProvider')}
                  </span>

                  <span className="typo-body-bold-small w-full whitespace-nowrap">
                    {t(
                      `pages.currentProfiles.cards.settings.digitizedCards.values.providers.${transaction.tokenRequestor || 'APPLE_PAY'}`,
                    )}
                  </span>
                </div>

                <div className="flex align-baseline items-center justify-between w-full gap-3">
                  <span className="typo-body-regular-small min-w-[5rem]">
                    {t('pages.currentProfiles.cards.settings.digitizedCards.headers.deviceName')}
                  </span>

                  <span className="typo-body-bold-small w-full whitespace-nowrap">
                    {t(
                      `pages.currentProfiles.cards.settings.digitizedCards.values.devices.${transaction.deviceType || 'A'}`,
                    )}{' '}
                    | {transaction.deviceName}
                  </span>
                </div>
              </div>
              <Divider className="text-onColor" />
              <span className="typo-body-medium-small whitespace-nowrap">
                {t('pages.currentProfiles.cards.transactions.table.tooltips.seeDigitizedCard')}
              </span>
            </div>
          }
          contentClassName="p-4 min-w-[180px] max-w-[320px]"
        >
          <div
            className="flex items-center gap-2 text-icon-primary bg-tag-gray rounded-100 px-3 py-2 cursor-pointer hover:text-interactive"
            data-testId="toggle-card-setting"
            onClick={togglePaymentCardSettingsDialog}
          >
            <span className="text-sm font-bold">
              {t('pages.currentProfiles.cards.transactions.table.digitizedCard')}
            </span>
            <Icon name="chevron_right" size="s" />
          </div>
        </Tooltip>
      ) : (
        <div className="flex items-center gap-2 bg-tag-gray rounded-100 px-3 py-2">
          <span className="text-sm font-bold text-secondary">
            {t('pages.currentProfiles.cards.transactions.table.digitizedCard')}
          </span>
        </div>
      )}
    </>
  );
}
